import { euclideanModulo } from '@goodboydigital/odie';
import gsap from 'gsap';
import { Container, InteractionEvent, Sprite, Texture } from 'pixi.js';
import { Signal } from 'typed-signals';

import { Shared } from '../App';
import { Settings } from '../AppConfig';
import Curve from '../utils/Curve';
import { Maths } from '../utils/Maths';
import { RacePoster } from './RacePoster';
import { Widget } from './Widget';

export class RaceMap extends Widget
{
    private content: Container;
    private posters: RacePoster[];
    private position = 0;
    private spacing = 230;
    private length: number;
    private area: Sprite;
    private bar: Sprite;
    private fill: Sprite;
    private posterContainer: Container;
    private pointerVelX = 0;
    private pointerX = 0;
    private pressing = false;
    private dragging = false;
    public limit = 1;

    private snapStart = 0;
    private snapEnd = 0;
    private snapDuration = 0;
    private snapTime = 0;

    public onSnap: Signal<() => void>;
    public onStartDrag: Signal<() => void>;
    public onStopDrag: Signal<() => void>;
    public onTapSelected: Signal<(race: number) => void>;

    constructor()
    {
        super();
        this.posters = [];
        this.length = Settings.maxRaces;

        this.onSnap = new Signal();
        this.onStartDrag = new Signal();
        this.onStopDrag = new Signal();
        this.onTapSelected = new Signal();

        this.content = new Container();
        this.view.addChild(this.content);

        this.bar = new Sprite(Shared.assets.getTexture('race-progress-empty'));
        this.content.addChild(this.bar);
        this.bar.anchor.set(0.5);
        this.bar.scale.x = 100;
        this.bar.y = 46;

        this.fill = new Sprite(Shared.assets.getTexture('race-progress-full'));
        this.fill.anchor.set(1, 0.5);
        this.fill.scale.x = this.bar.scale.x;
        this.fill.y = this.bar.y;
        this.content.addChild(this.fill);

        this.area = new Sprite(Texture.WHITE);
        this.content.addChild(this.area);
        this.area.anchor.set(0.5);
        this.area.alpha = 0;
        this.area.width = 9999;
        this.area.height = 300;
        this.area.interactive = true;
        this.area.on('pointerdown', this.onPointerDown.bind(this));
        this.area.on('pointermove', this.onPointerMove.bind(this));
        this.area.on('pointerup', this.onPointerUp.bind(this));
        this.area.on('pointerupoutside', this.onPointerUp.bind(this));
        this.area.on('pointercancel', this.onPointerUp.bind(this));

        this.posterContainer = new Container();
        this.content.addChild(this.posterContainer);

        for (let i = 0; i < 10; i++) this.addPoster();
    }

    private onPointerDown(e: InteractionEvent): void
    {
        this.pressing = true;
        this.dragging = false;
        this.pointerX = e.data.global.x;
        this.pointerVelX = 0;
    }

    private onPointerMove(e: InteractionEvent): void
    {
        if (!this.pressing) return;
        this.pointerVelX = e.data.global.x - this.pointerX;
        this.pointerX = e.data.global.x;
        this.position += this.pointerVelX * 0.003;
        this.position = this.clampToLimits(this.position);
        if (!this.dragging)
        {
            this.dragging = true;
            this.onStartDrag.emit();
        }
    }

    private onPointerUp(): void
    {
        if (!this.pressing) return;
        this.pressing = false;
        this.snap(this.position + (this.pointerVelX * 0.05));
        if (this.dragging)
        {
            this.dragging = false;
            this.onStopDrag.emit();
        }
    }

    private clampToLimits(v: number): number
    {
        return Maths.clamp(v, 0, this.limit);
    }

    public getIndex(): number
    {
        return Math.round(-this.position);
    }

    public setIndex(v: number): void
    {
        this.position = -v;
        this.position = this.clampToLimits(this.position);
    }

    public setMaxIndex(v: number): void
    {
        this.limit = -v;
    }

    public update(delta = 1): void
    {
        const spacing = this.spacing;
        const length = this.length;
        const offsetX = spacing * 3;

        if (!this.pressing && this.snapDuration && this.snapTime < this.snapDuration)
        {
            this.snapTime += delta * (1 / 60);
            let snapped = false;

            if (this.snapTime > this.snapDuration)
            {
                this.snapTime = this.snapDuration;
                snapped = true;
            }
            this.position = Maths.tween(this.snapStart, this.snapEnd, Curve.backOut(this.snapTime / this.snapDuration));

            if (snapped)
            {
                this.snapDuration = 0;
                this.snapTime = 0;
                this.onSnap.emit();
            }
        }

        for (let i = 0; i < this.posters.length; i++)
        {
            const item = this.posters[i];
            const offset = euclideanModulo(this.position, 1);
            const relativePos = Math.ceil(-this.position) + i - 4;

            item.view.renderable = relativePos > -1;

            const index = euclideanModulo(relativePos, length);

            item.view.x = (spacing * (i + offset)) - spacing - offsetX;
            item.index = index;

            const step = Maths.clamp(1 - Math.abs(item.view.x * 0.0045));
            const race = index + 1;
            const position = Shared.user.getSavedPositionByRace(race);
            const players = Shared.user.getSavedPlayersByRace(race);
            const time = Shared.user.getSavedTimeByRace(race);
            const raceType = Shared.game.race.getRaceTypeByIndex(race);
            const nemesisCount = Shared.game.race.getNemesisCountByIndex(race);

            item.setup({ race, position, players, raceType, step, time, nemesisCount });

            // Post positioning
            item.view.x += 64 * Maths.signNon0(item.view.x) * (1 - step);
            item.view.y = 46 * (1 - step);
            item.update(delta);

            // Update yellow fill position
            if (position) this.fill.x = item.view.x + this.spacing;
            item.view.interactive = race <= -this.limit + 1;
            item.view.buttonMode = item.view.interactive;
        }
    }

    public snap(position: number, duration = 0.75): void
    {
        this.snapStart = this.position;
        this.snapEnd = Math.round(position);
        this.snapEnd = this.clampToLimits(this.snapEnd);
        this.snapTime = 0;
        this.snapDuration = duration;
    }

    private addPoster(): RacePoster
    {
        const poster = new RacePoster();

        this.posterContainer.addChild(poster.view);
        this.posters.push(poster);
        poster.open();

        poster.view.on('pointertap', () => this.onSelectPoster(poster));
        poster.view.on('pointerdown', this.onPointerDown.bind(this));
        poster.view.on('pointerup', this.onPointerUp.bind(this));
        poster.view.on('pointerupoutside', this.onPointerUp.bind(this));
        poster.view.on('pointercancel', this.onPointerUp.bind(this));

        return poster;
    }

    private onSelectPoster(poster: RacePoster): void
    {
        const position = -poster.data.race + 1;

        if (position === this.position)
        {
            this.onTapSelected.emit(poster.data.race);
        }
        else
        {
            this.snap(-poster.data.race + 1, 0.5);
        }
    }

    protected async onOpen(): Promise<void>
    {
        gsap.killTweensOf(this.content);
        this.content.y = 500;
        await gsap.to(this.content, { y: 0, duration: 0.4, ease: 'back.out' });
    }

    protected async onClose(): Promise<void>
    {
        gsap.killTweensOf(this.content);
        await gsap.to(this.content, { y: 500, duration: 0.3, ease: 'back.in' });
    }
}
